import React from "react";

/**
 * Allow a component to be either internally or externally controlled, where
 * specifying a controlled value requires you to also specify a change hook for
 * that value, and vice versa
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type HybridField<T extends {} | null> =
  | ({
      [Property in keyof T as `on${Capitalize<
        string & Property
      >}Change`]: React.Dispatch<React.SetStateAction<T[Property]>>;
    } & T)
  | ({
      [Property in keyof T as `on${Capitalize<
        string & Property
      >}Change`]?: never;
    } & {
      [Property in keyof T]?: never;
    });

type useHybridFieldValue<T> = {
  initialValue: T;
  value?: T;
  onValueChange?: React.Dispatch<React.SetStateAction<T>>;
};

/**
 * Hook to allow a component to be either internally or externally controlled.
 * You MUST use the HybridField type on the props of the component you are using
 * this hook in order for the type system to work correctly.
 */
export const useHybridState = <T>({
  initialValue,
  value,
  onValueChange,
}: useHybridFieldValue<T>) => {
  const [internal, setInternal] = React.useState(initialValue);

  // This non-null assertion assumes value and onValueChange are from a
  // HybridField prop. This was really hard to enfore via the type system.
  if (onValueChange !== undefined) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return [value!, onValueChange] as const;
  }
  return [internal, setInternal] as const;
};
